import css from './SpeechButton.module.scss';

import { useRef, useState, memo, useEffect } from 'react';

import { useAudioBlob } from 'core/helpers/audio';
import { isAbortError, isNotAllowedError } from 'core/helpers/browser';
import useLocalStorageState from 'core/hooks/browser/useLocalStorageState';
import { AUTO_SPEECH, AUTO_SPEECH_DEFAULT } from 'core/options';

import { useMessageModal } from '../MessageModal';
import { Icon } from '../icons';

type TProps = {
  wordUid: string;
  disabled: boolean;
  className?: string;
};

const SpeechButton: React.FC<TProps> = ({
  wordUid,
  disabled,
  className = '',
}) => {
  const [isAutoPlay] = useLocalStorageState(AUTO_SPEECH, AUTO_SPEECH_DEFAULT);
  const audio = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const { showError } = useMessageModal();

  useEffect(() => () => audio.current?.pause(), [wordUid]);

  const play = () => {
    if (disabled || !blobUrl) return;

    const node = orDie(audio.current);
    node.pause();
    node.currentTime = 0;
    node
      .play()
      .catch(
        (e: unknown) =>
          !isAbortError(e) && !isNotAllowedError(e) && showError(e)
      );
  };

  const blobUrl = useAudioBlob(wordUid);

  useEffect(() => {
    if (!disabled && isAutoPlay) play();
  }, [disabled, blobUrl]);

  return (
    <div
      data-loaded={!!blobUrl}
      data-playing={isPlaying}
      className={`${css.speechButton} ${className}`}
    >
      <Icon icon="PlaySound" className={css.icon} onClick={play} />
      {blobUrl && (
        <audio
          ref={audio}
          autoPlay={isAutoPlay && !disabled}
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          src={blobUrl}
          preload="auto"
        />
      )}
    </div>
  );
};

export default memo(SpeechButton);
